import { useEffect, useState } from 'react';

import clsx from 'clsx';
import { parseCookies, setCookie } from 'nookies';

import { Container } from 'components/Grid';
import Skeleton from 'components/Skeleton';
import { useEventContext } from 'context/EventContext';
import detachTask from 'helpers/detachTask';

import HomeButton from '../HomeButton';
import HomeHeading from '../HomeHeading';
import CategoryDropdown from './components/CategoryDropdown';
import Grid from './components/Grid';
import ListingItem from './components/ListingItem';
import ListingsFilter from './components/ListingsFilter';
import { CATEGORIES, FILTERS } from './constants';

import classes from './Explore.module.scss';

const REQUEST_STATUS = {
  idle: 0,
  loading: 1,
  error: 3,
};

const SELECTED_CATEGORY_COOKIE = 'homepage_explore_category';
const SELECTED_SORT_COOKIE = 'homepage_explore_sort';
const DEFAULT_SORT = FILTERS[0].value;
const EMPTY_POPULAR_CATEGORY = { listings: [], slug: CATEGORIES[0].slug };

const fetchListingsByCategory = (categorySlug, sort = DEFAULT_SORT) =>
  fetch(
    `/apiui/home/category-listings/?${new URLSearchParams({ slug: categorySlug, sort })}`
  ).then((result) => result.json());

/** @type {import("./types").default} */
const Explore = ({ popularCategory }) => {
  const category = popularCategory ?? EMPTY_POPULAR_CATEGORY;
  const [selectedFilter, setSelectedFilter] = useState(DEFAULT_SORT);
  const [selectedCategory, setSelectedCategory] = useState(category.slug);
  const [requestStatus, setRequestStatus] = useState(REQUEST_STATUS.idle);
  const [listings, setListings] = useState(category.listings);
  const currentCategory = CATEGORIES.find((c) => c.slug === selectedCategory);

  const updateListings = (categorySlug, sort) => {
    setRequestStatus(REQUEST_STATUS.loading);
    fetchListingsByCategory(categorySlug, sort)
      .then((result) => {
        const fetchedListings = result?.data?.category_hero_listings || [];
        if (fetchedListings.length > 0) {
          setRequestStatus(REQUEST_STATUS.idle);
          setListings(fetchedListings);
        } else {
          setRequestStatus(REQUEST_STATUS.error);
        }
      })
      .catch(() => {
        setRequestStatus(REQUEST_STATUS.error);
      });
  };

  // Setting value in useEffect to avoid next.js values mismatch warning
  useEffect(() => {
    const {
      [SELECTED_CATEGORY_COOKIE]: selectedCategorySlugCookie,
      [SELECTED_SORT_COOKIE]: selectedSortCookie,
    } = parseCookies();

    const categoryChanged =
      selectedCategorySlugCookie &&
      selectedCategorySlugCookie !== category.slug;
    const sortFromCookie = selectedSortCookie || DEFAULT_SORT;

    // Update selected filter state from cookie
    if (selectedSortCookie && selectedSortCookie !== selectedFilter) {
      setSelectedFilter(selectedSortCookie);
    }

    // Fetch listings if category changed
    if (categoryChanged) {
      setSelectedCategory(selectedCategorySlugCookie);
      updateListings(selectedCategorySlugCookie, sortFromCookie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const eventContext = useEventContext({
    engagementComponent: 'category-selector',
  });

  const handleOnSelectCategory = (category) => {
    eventContext.sendEvent({
      engagementDestination: 'select_category',
      engagementDetails: 'category-selector',
      name: 'engagement_facet_click',
    });

    setSelectedCategory(category);
    updateListings(category, selectedFilter);
    // Adding explicit check because this function gets called even if values are the same
    if (category !== selectedCategory) {
      setCookie(null, SELECTED_CATEGORY_COOKIE, category, {
        path: '/',
      });
    }
  };

  const handleOnSelectFilter = (filter) => {
    setSelectedFilter(filter);
    updateListings(selectedCategory, filter);
    // Save sort preference to cookie
    if (filter !== selectedFilter) {
      setCookie(null, SELECTED_SORT_COOKIE, filter, {
        path: '/',
      });
    }
  };

  return (
    <div className={clsx('Explore', classes.root)}>
      <Container>
        <HomeHeading>Explore tech solutions users trust</HomeHeading>
        <CategoryDropdown
          categories={CATEGORIES}
          selected={selectedCategory}
          onSelect={(category) => detachTask(handleOnSelectCategory, category)}
        />
        <ListingsFilter
          filters={FILTERS}
          selected={selectedFilter}
          onSelect={(filter) => detachTask(handleOnSelectFilter, filter)}
        />
        <div>
          {requestStatus === REQUEST_STATUS.idle && listings.length ? (
            <>
              <Grid>
                {listings.map((listing) => (
                  <ListingItem
                    key={`popularListing-${listing.id}`}
                    listing={listing}
                  />
                ))}
              </Grid>
              <div className={classes.buttonsContainer}>
                <HomeButton
                  data-testid="explore-see-more"
                  href={currentCategory?.url}
                  {...eventContext.addEvent({
                    engagementDestination: 'go-to_category_all-products',
                    engagementDetails: 'text-link_see-all-softwares',
                    name: 'engagement_facet_click',
                  })}
                >
                  See all {currentCategory?.label}
                </HomeButton>
              </div>
            </>
          ) : requestStatus === REQUEST_STATUS.loading ? (
            <>
              <Grid>
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`popularListing-placeHolder-${i}`}
                    variant="rect"
                    className={classes.placeholderItem}
                  />
                ))}
              </Grid>
              <div className={classes.buttonsContainer}>
                <Skeleton variant="rect" className={classes.placeholderLink} />
              </div>
            </>
          ) : (
            <p>Connectivity problems, please try again!</p>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Explore;
